import { useState, useMemo, useEffect, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { EditableText } from '@/components/risk/EditableText'
import { Boxes, Search, Filter, ChevronUp, ChevronDown, Trash2 } from 'lucide-react'
import { projectsApi, type AssetInventoryRow } from '@/lib/projectsApi'
import { useDirtyForm } from '@/contexts/DirtyFormContext'
import { useToast } from '@/contexts/ToastContext'
import { useTranslation } from 'react-i18next'

type ColumnType = 'text' | 'number' | 'boolean' | 'long-text'

interface ColumnDef {
  key: keyof AssetInventoryRow
  labelKey: string
  width?: string
  compactHidden?: boolean
  filterable?: boolean
  type: ColumnType
}

const COLUMNS: ColumnDef[] = [
  { key: 'item_number',                    labelKey: 'item',         width: 'w-12',  type: 'number',    filterable: false },
  { key: 'ship_functions_and_systems',     labelKey: 'function',     width: 'w-40',  type: 'text',      filterable: true  },
  { key: 'system',                         labelKey: 'system',       width: 'w-40',  type: 'text',      filterable: true  },
  { key: 'equipment',                      labelKey: 'equipment',    width: 'w-48',  type: 'text',      filterable: true  },
  { key: 'system_category',                labelKey: 'category',     width: 'w-20',  type: 'text',      filterable: true  },
  { key: 'brand_manufacturer',             labelKey: 'vendor',       width: 'w-32',  type: 'text',      filterable: true  },
  { key: 'model_type',                     labelKey: 'model',        width: 'w-32',  type: 'text' },
  { key: 'unique_identifier',              labelKey: 'id',           width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'os_version_patch',               labelKey: 'os',           width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'firmware_version_patch',         labelKey: 'firmware',     width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'software_version',               labelKey: 'software',     width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'location',                       labelKey: 'location',     width: 'w-32',  type: 'text',      filterable: true },
  { key: 'security_zone',                  labelKey: 'zone',         width: 'w-28',  type: 'text',      filterable: true },
  { key: 'description_purpose',            labelKey: 'description', width: 'w-64',  type: 'long-text', compactHidden: true },
  { key: 'connections_within_scope',       labelKey: 'connectionIn', width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'connections_untrusted_networks', labelKey: 'connectionOut', width: 'w-32', type: 'text',      compactHidden: true },
  { key: 'physical_interfaces',            labelKey: 'physical',     width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'communication_protocols',        labelKey: 'protocols',    width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'ip_ranges_mac_addresses',        labelKey: 'ipMac',        width: 'w-32',  type: 'text',      compactHidden: true },
  { key: 'certificate_type_approval',      labelKey: 'taCertificate', width: 'w-32', type: 'text' },
  { key: 'excluded',                       labelKey: 'excluded',      width: 'w-16',  type: 'boolean' },
  { key: 'update_log',                     labelKey: 'updated',       width: 'w-32',  type: 'text',      compactHidden: true },
]

interface Props {
  rows: AssetInventoryRow[]
  canEdit: boolean
  // refetch escape hatch, call when an inline edit fails so the page
  // reloads from the server and reconciles
  onChanged: () => void
  // optimistic single-row replacement on inline-edit success. avoids the
  // full project refetch (and the flicker / scroll jump) for super
  // admins doing rapid sequential edits
  onRowUpdated?: (row: AssetInventoryRow) => void
  // initial value for the Full/Compact toggle. defaults to 'full' so the
  // project-level page's existing behaviour is unchanged. system-detail
  // page passes 'compact' since its bespoke pre-refactor table only
  // showed the compact column set. defaulting to 'full' there would've
  // surprised existing users with a wall of fine-print columns.
  defaultViewMode?: 'full' | 'compact'
}

export function AssetInventoryTab({ rows, canEdit, onChanged, onRowUpdated, defaultViewMode = 'full' }: Props) {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<'full' | 'compact'>(defaultViewMode)
  const [searchInput, setSearchInput] = useState('')
  const [globalSearch, setGlobalSearch] = useState('')
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({})
  const [sortBy, setSortBy] = useState<keyof AssetInventoryRow | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const { error: showError, success } = useToast()

  // #6 delete affordances target MANUAL rows only. Word-RA-written rows
  // (source='word_ra_inventory') are managed by the mapping (Q4), read-only
  // here. user must unmap the assessment to remove them.
  const manualRows = useMemo(() => rows.filter(r => !r.source), [rows])
  const projectId = useMemo(() => rows.find(r => r.project_id)?.project_id ?? null, [rows])

  const deleteRow = async (row: AssetInventoryRow) => {
    if (!row.id) return
    if (!window.confirm(t('assetInventory.confirmDeleteRow', { name: row.system ?? row.equipment ?? '—' }))) return
    setBusyId(row.id)
    try {
      await projectsApi.deleteAssetInventoryRow(row.id)
      onChanged()
    } catch (e: any) {
      showError(e?.message ?? t('assetInventory.deleteFailed'))
    } finally {
      setBusyId(null)
    }
  }

  const clearManual = async () => {
    if (!projectId) return
    if (!window.confirm(t('assetInventory.confirmClearManual', { count: manualRows.length }))) return
    try {
      const r = await projectsApi.clearManualAssetInventory(projectId)
      success(t('assetInventory.removedRows', { count: r.deleted }))
      onChanged()
    } catch (e: any) {
      showError(e?.message ?? t('assetInventory.clearFailed'))
    }
  }

  // debounce global search 200ms so typing doesn't refilter every keystroke
  useEffect(() => {
    const id = setTimeout(() => setGlobalSearch(searchInput), 200)
    return () => clearTimeout(id)
  }, [searchInput])

  const visibleColumns = useMemo(
    () => viewMode === 'compact' ? COLUMNS.filter(c => !c.compactHidden) : COLUMNS,
    [viewMode],
  )

  const distinctValuesFor = (col: keyof AssetInventoryRow): string[] => {
    const set = new Set<string>()
    for (const row of rows) {
      const v = (row as any)[col]
      set.add(v == null ? '' : String(v))
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }

  const filtered = useMemo(() => {
    let result = rows
    const q = globalSearch.trim().toLowerCase()
    if (q) {
      result = result.filter(row =>
        Object.values(row).some(v => v != null && String(v).toLowerCase().includes(q)),
      )
    }
    for (const [col, vals] of Object.entries(columnFilters)) {
      if (!vals || vals.length === 0) continue
      const set = new Set(vals)
      result = result.filter(row => {
        const v = (row as any)[col]
        return set.has(v == null ? '' : String(v))
      })
    }
    if (sortBy) {
      const dir = sortDir === 'asc' ? 1 : -1
      result = [...result].sort((a, b) => {
        const av = (a as any)[sortBy]
        const bv = (b as any)[sortBy]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        if (typeof av === 'boolean' && typeof bv === 'boolean') return (Number(av) - Number(bv)) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    }
    return result
  }, [rows, globalSearch, columnFilters, sortBy, sortDir])

  const hasActiveColumnFilters = Object.values(columnFilters).some(v => v && v.length > 0)

  return (
    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Boxes className="h-4 w-4 text-slate-400" />
            {t('assetInventory.title')}
            <span className="text-xs font-normal text-slate-400">
              {t('assetInventory.showing', { shown: filtered.length, total: rows.length })}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={t('assetInventory.searchPlaceholder')}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
              />
            </div>
            <div className="inline-flex rounded bg-slate-100 dark:bg-slate-900 p-0.5">
              {(['full', 'compact'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setViewMode(m)}
                  className={
                    'px-2.5 py-1 text-xs rounded transition ' +
                    (viewMode === m
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200')
                  }
                >
                  {m === 'full' ? t('assetInventory.full') : t('assetInventory.compact')}
                </button>
              ))}
            </div>
            {hasActiveColumnFilters && (
              <button
                onClick={() => setColumnFilters({})}
                className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              >
                {t('assetInventory.clearFilters')}
              </button>
            )}
            {canEdit && manualRows.length > 0 && (
              <button
                onClick={clearManual}
                title={t('assetInventory.removeImportedTitle')}
                className="text-xs px-2 py-1 text-red-600 hover:text-red-700 inline-flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" /> {t('assetInventory.removeImported', { count: manualRows.length })}
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900/40 sticky top-0 z-10">
              <tr>
                {visibleColumns.map(col => {
                  const colKey = col.key as string
                  return (
                    <th
                      key={colKey}
                      className={`${col.width ?? ''} px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap border-b border-slate-200 dark:border-slate-700`}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (sortBy === col.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
                            else { setSortBy(col.key); setSortDir('asc') }
                          }}
                          className="hover:text-slate-900 dark:hover:text-white inline-flex items-center gap-0.5"
                        >
                          {t(`assetInventory.columns.${col.labelKey}`)}
                          {sortBy === col.key && (
                            sortDir === 'asc'
                              ? <ChevronUp className="h-3 w-3" />
                              : <ChevronDown className="h-3 w-3" />
                          )}
                        </button>
                        {col.filterable && (
                          <FilterDropdown
                            values={distinctValuesFor(col.key)}
                            selected={columnFilters[colKey] ?? []}
                            onChange={selected => setColumnFilters(f => ({ ...f, [colKey]: selected }))}
                            open={openFilterCol === colKey}
                            onToggle={() => setOpenFilterCol(o => o === colKey ? null : colKey)}
                          />
                        )}
                      </div>
                    </th>
                  )
                })}
                <th className="w-40 px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap border-b border-slate-200 dark:border-slate-700">
                  {t('assetInventory.linked')}
                </th>
                <th className="w-20 px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap border-b border-slate-200 dark:border-slate-700" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {filtered.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  {visibleColumns.map(col => (
                    <td key={col.key as string} className="px-2 py-1.5 align-top">
                      <Cell
                        row={row}
                        col={col}
                        canEdit={canEdit}
                        onSave={async (newVal) => {
                          if (!row.id) return
                          const updated = await projectsApi.updateAssetInventoryRow(
                            row.id,
                            { [col.key]: newVal } as Partial<AssetInventoryRow>,
                          )
                          // optimistic path: server returned the updated row,
                          // splice it into local state. if server returned
                          // null (no row matched) fall back to a full refetch
                          // so the UI reconciles
                          if (updated && onRowUpdated) onRowUpdated(updated)
                          else onChanged()
                        }}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1.5 align-top text-slate-500 dark:text-slate-400">
                    {row.linked_system_name ?? <span className="text-slate-300 dark:text-slate-600 italic">—</span>}
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    {row.source === 'word_ra_inventory' ? (
                      <span
                        title={t('assetInventory.wordRaManagedTitle')}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      >
                        {t('assetInventory.fromWordRa')}
                      </span>
                    ) : canEdit ? (
                      <button
                        onClick={() => deleteRow(row)}
                        disabled={busyId === row.id}
                        title={t('assetInventory.deleteRow')}
                        className="text-slate-400 hover:text-red-500 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 2}
                      className="px-3 py-12 text-center text-sm text-slate-400">
                    {rows.length === 0
                      ? t('assetInventory.empty')
                      : t('assetInventory.noMatch')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function Cell({ row, col, canEdit, onSave }: {
  row: AssetInventoryRow
  col: ColumnDef
  canEdit: boolean
  onSave: (v: unknown) => Promise<void>
}) {
  const { t } = useTranslation()
  const dirty = useDirtyForm()
  const v = (row as any)[col.key]

  // build the dirty descriptor once. systems_190.id is the pk, column
  // key is the field. only meaningful when a DirtyFormProvider is mounted.
  // EditableText / boolean toggle inspect dirty.isUnderDirtyForm to decide
  // whether to stage or call onSave directly
  const dirtyKey = row.id ? { table: 'systems_190', id: row.id, field: col.key as string } : undefined
  const draftEntry = dirtyKey && dirty.isUnderDirtyForm
    ? dirty.getDraft(dirtyKey.table, dirtyKey.id, dirtyKey.field)
    : undefined
  const displayValue: unknown = draftEntry ? draftEntry.draftValue : v
  const isDirty = draftEntry !== undefined

  if (col.type === 'boolean') {
    if (canEdit) {
      const onToggle = () => {
        const next = !displayValue
        if (dirty.isUnderDirtyForm && dirtyKey) {
          dirty.setDraft(dirtyKey.table, dirtyKey.id, dirtyKey.field, v, next)
          return
        }
        void onSave(next)
      }
      return (
        <button
          type="button"
          onClick={onToggle}
          title={displayValue ? t('assetInventory.clickInclude') : t('assetInventory.clickExclude')}
          className={
            'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium transition ' +
            (displayValue
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-600') +
            (isDirty ? ' ring-1 ring-amber-300 dark:ring-amber-600' : '')
          }
        >
          {displayValue ? t('assetInventory.excludedShort') : '—'}
        </button>
      )
    }
    return displayValue
      ? <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">{t('assetInventory.excludedShort')}</span>
      : <span className="text-slate-300 dark:text-slate-600">—</span>
  }

  if (col.key === 'item_number') {
    return <span className="text-slate-500 tabular-nums">{v ?? '—'}</span>
  }

  if (canEdit) {
    return (
      <EditableText
        value={v == null ? null : String(v)}
        onSave={async (nv) => onSave(nv)}
        multiline={col.type === 'long-text'}
        emptyText="—"
        dirtyKey={dirtyKey}
      />
    )
  }

  if (v == null || v === '') {
    return <span className="text-slate-300 dark:text-slate-600">—</span>
  }
  return <span className="text-slate-700 dark:text-slate-300 break-words">{String(v)}</span>
}

function FilterDropdown({ values, selected, onChange, open, onToggle }: {
  values: string[]
  selected: string[]
  onChange: (next: string[]) => void
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const isFiltered = selected.length > 0
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onToggle])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        className={'p-0.5 rounded ' + (isFiltered ? 'text-blue-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200')}
      >
        <Filter className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-20 w-56 max-h-72 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-lg">
          <div className="p-2 border-b border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >{t('common.clear')}</button>
            <button
              type="button"
              onClick={() => onChange(values)}
              className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >{t('common.selectAll')}</button>
          </div>
          <div className="py-1">
            {values.map(v => {
              const checked = selected.includes(v)
              return (
                <label
                  key={v}
                  className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? selected.filter(s => s !== v) : [...selected, v])}
                    className="rounded text-blue-500"
                  />
                  <span className="truncate text-slate-700 dark:text-slate-300">
                    {v === '' ? <span className="italic text-slate-400">{t('assetInventory.emptyValue')}</span> : v}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
